import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Avatar } from 'primereact/avatar';
import { environment } from './pages/util/baseUrl';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import './AppTopbar.css';
import { SystemConfig } from './config/SystemConfig';
import { useTheme } from './context/ThemeContext';
import { SucursalService } from './services/SucursalService';

export const AppTopbar = (props) => {
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const { logout, user, updateUserProfile } = useAuth();
    const { darkMode, toggleDarkMode } = useTheme();
    const [sucursales, setSucursales] = useState([]);
    
    useEffect(() => {
        // Si el usuario es multi-sucursal o puede elegir...
        if (!user?.idSucursalOriginal) {
            SucursalService.getAll().then(res => {
                setSucursales(res.data || res.items || []);
            }).catch(console.error);
        }
    }, [user?.idSucursalOriginal]);

    // Obtener nombre de usuario del contexto
    const userName = user?.username || 'Usuario';

    // Generar iniciales del nombre
    const initials = useMemo(() => {
        return userName
            .substring(0, 2)
            .toUpperCase();
    }, [userName]);

    const menuItems = useMemo(() => {
        // Items del menú desplegable base
        const items = [
            {
                label: 'Ver perfil',
                icon: 'pi pi-user',
                command: () => navigate('/perfil'),
            },
            { separator: true },
            {
                label: darkMode ? 'Modo Claro' : 'Modo Oscuro',
                icon: darkMode ? 'pi pi-sun' : 'pi pi-moon',
                command: toggleDarkMode,
            }
        ];

        // Añadir el submenú de sucursales si es un super admin (Multi-sucursal)
        if (!user?.idSucursalOriginal && sucursales.length > 0) {
            items.push({ separator: true });
            
            const sucursalesItems = sucursales.map(suc => ({
                label: suc.nombre + (user?.idSucursal === suc.id ? ' (Activa)' : ''),
                icon: user?.idSucursal === suc.id ? 'pi pi-check-circle' : 'pi pi-building',
                command: () => {
                    updateUserProfile({ idSucursal: suc.id });
                    window.location.reload(); // Recargar para aplicar los datos a la nueva sucursal globalmente
                }
            }));

            // La opción para ver "Todas" nuevamente
            sucursalesItems.unshift({
                label: 'Vista Global (Todas)' + (!user?.idSucursal ? ' (Activa)' : ''),
                icon: !user?.idSucursal ? 'pi pi-check-circle' : 'pi pi-globe',
                command: () => {
                    updateUserProfile({ idSucursal: null });
                    window.location.reload();
                }
            });

            items.push({
                label: 'Cambiar Sucursal de Trabajo',
                icon: 'pi pi-map-marker',
                items: sucursalesItems
            });
        }

        items.push({ separator: true });
        items.push({
            label: 'Cerrar sesión',
            icon: 'pi pi-sign-out',
            command: async () => {
                await logout();
                navigate('/login');
            }
        });

        return items;
    }, [navigate, logout, darkMode, toggleDarkMode, user?.idSucursal, user?.idSucursalOriginal, sucursales, updateUserProfile]);

    const toggleMenu = (event) => {
        if (menuRef.current) {
            menuRef.current.toggle(event);
        }
    };

    return (
        <header className="app-topbar">
            <div className="topbar-left">
                <Button
                    type="button"
                    icon="pi pi-bars"
                    className="p-button-text p-button-rounded mr-2"
                    style={{ color: 'var(--topbar-text)' }}
                    onClick={props.onToggleMenuClick}
                />
                <div className="logo-container">
                    <img src={SystemConfig.logoPath} alt="Logo" height="40" />
                </div>
                <span className="logo-text" style={{ color: 'var(--topbar-text)' }}>{SystemConfig.appName}</span>
            </div>
            <div className="topbar-right">
                <div className="user-info">
                    <span className="welcome-text" style={{ color: 'var(--topbar-text)', opacity: 0.8 }}>Bienvenido,</span>
                    <div className="user-name" style={{ color: 'var(--topbar-text)' }}>{userName}</div>
                </div>
                <Avatar
                    image={user?.profileImage ? (user.profileImage.startsWith('/') ? `${environment.backendUrl}${user.profileImage}` : user.profileImage) : null}
                    label={!user?.profileImage ? (initials || '?') : null}
                    shape="circle"
                    className="user-avatar"
                    aria-label="Avatar del usuario"
                />
                <Button
                    type="button"
                    className="p-button-text p-button-rounded menu-toggle"
                    icon="pi pi-angle-down"
                    style={{ color: 'var(--topbar-text)' }}
                    onClick={toggleMenu}
                    aria-label="Abrir menú de usuario"
                />
                <Menu
                    model={menuItems}
                    popup
                    ref={menuRef}
                />
            </div>
        </header>
    );
};

